import ApiClient from 'api-client/ApiClient'

export const CUSTOM_FLAVOR_EXTRA_SPEC_KEY = 'pcd:custom_flavor'

interface CreateCustomFlavorParams {
  name: string
  vcpus: number
  ramMiB: number
  diskGiB: number
  tenantId: string
}

const { nova } = ApiClient.getInstance()

// CFH-FR-001: create a private, on-the-fly flavor, tag it so it can be identified later (Custom
// badge, dedup correctness), and grant the requesting tenant access to it. A private Nova flavor's
// access list starts empty — not even the creating project can use it — so without this grant the
// VM create that immediately follows would fail to boot from its own just-created flavor (mirrors
// CreateFlavorModal.tsx's addFlavorTenant call for private flavors). Mirrors prepareIsoBootVolume's
// contract: rejects on failure so the caller can treat it like any other pre-create failure.
export const createCustomFlavor = async ({
  name,
  vcpus,
  ramMiB,
  diskGiB,
  tenantId,
}: CreateCustomFlavorParams): Promise<string> => {
  const created = await nova.createFlavor({
    flavor: {
      name,
      vcpus,
      ram: ramMiB,
      disk: diskGiB,
      'os-flavor-access:is_public': false,
    },
  })
  await nova.createFlavorMetadata({
    id: created.id,
    body: { extra_specs: { [CUSTOM_FLAVOR_EXTRA_SPEC_KEY]: 'true' } },
  })
  await nova.addFlavorTenant({
    id: created.id,
    body: { addTenantAccess: { tenant: tenantId } },
  })
  return created.id
}

// CFH-FR-002: best-effort delete, same contract as cleanupIsoBootVolume — never throw, never
// mask an original failure.
export const cleanupCustomFlavor = async (flavorId?: string): Promise<void> => {
  if (!flavorId) {
    return
  }
  try {
    await nova.deleteFlavor(flavorId)
  } catch {
    // Best effort — swallow. If the VM already succeeded, we never want a cleanup error to
    // surface and look like the VM creation itself failed.
  }
}

// CFH-FR-003: fixed name for flavors created without a user-provided name (i.e. "Save this
// flavor" left unchecked). It's temporary — deleted right after the VM is created
// (CVM-FR-023/024) — so it's never meant to be kept around under this name.
export const generateAutoFlavorName = (): string => 'custom'
